/**
 * OpenSimplex2 noise — TypeScript port of `noodle.geometry.SimplexNoise.kt`.
 *
 * Produces output that matches the Kotlin reference for any given seed and
 * input. The seeded shuffle uses 64-bit signed Long arithmetic (BigInt); the
 * noise hot path uses plain numbers (Double float + 32-bit bitwise).
 *
 * Adapted from KdotJPG's OpenSimplex2 (https://github.com/KdotJPG/OpenSimplex2).
 */

const PERM_SIZE = 2048;
const PERM_MASK = PERM_SIZE - 1;
const SKEW_2D = 0.366025403784439;
const UNSKEW_2D = -0.21132486540518713;
const NORM_2D = 32.69428253173828;

const MASK_64 = (1n << 64n) - 1n;
const SIGN_BIT = 1n << 63n;
const LCG_MULT = 6364136223846793005n;
const LCG_INC = 1442695040888963407n;

interface Grad2 {
  dx: number;
  dy: number;
}

const GRADIENTS_BASE: readonly Grad2[] = [
  { dx: 0.130526192220052, dy: 0.99144486137381 },
  { dx: 0.38268343236509, dy: 0.923879532511287 },
  { dx: 0.608761429008721, dy: 0.793353340291235 },
  { dx: 0.793353340291235, dy: 0.608761429008721 },
  { dx: 0.923879532511287, dy: 0.38268343236509 },
  { dx: 0.99144486137381, dy: 0.130526192220052 },
  { dx: 0.99144486137381, dy: -0.130526192220052 },
  { dx: 0.923879532511287, dy: -0.38268343236509 },
  { dx: 0.793353340291235, dy: -0.608761429008721 },
  { dx: 0.608761429008721, dy: -0.793353340291235 },
  { dx: 0.38268343236509, dy: -0.923879532511287 },
  { dx: 0.130526192220052, dy: -0.99144486137381 },
  { dx: -0.130526192220052, dy: -0.99144486137381 },
  { dx: -0.38268343236509, dy: -0.923879532511287 },
  { dx: -0.608761429008721, dy: -0.793353340291235 },
  { dx: -0.793353340291235, dy: -0.608761429008721 },
  { dx: -0.923879532511287, dy: -0.38268343236509 },
  { dx: -0.99144486137381, dy: -0.130526192220052 },
  { dx: -0.99144486137381, dy: 0.130526192220052 },
  { dx: -0.923879532511287, dy: 0.38268343236509 },
  { dx: -0.793353340291235, dy: 0.608761429008721 },
  { dx: -0.608761429008721, dy: 0.793353340291235 },
  { dx: -0.38268343236509, dy: 0.923879532511287 },
  { dx: -0.130526192220052, dy: 0.99144486137381 },
];

const GRADIENTS_2D: readonly Grad2[] = (() => {
  const out: Grad2[] = new Array(PERM_SIZE);
  for (let i = 0; i < PERM_SIZE; i++) out[i] = GRADIENTS_BASE[i % GRADIENTS_BASE.length];
  return out;
})();

function fastFloor(x: number): number {
  const xi = x | 0;
  return x < xi ? xi - 1 : xi;
}

/** Reinterpret unsigned 64-bit BigInt as signed (matches Kotlin Long). */
function toSignedI64(u: bigint): bigint {
  return u >= SIGN_BIT ? u - (1n << 64n) : u;
}

export class SimplexNoise {
  private readonly perm: Int16Array;
  private readonly permGradX: Float64Array;
  private readonly permGradY: Float64Array;

  constructor(seed: number | bigint) {
    this.perm = new Int16Array(PERM_SIZE);
    this.permGradX = new Float64Array(PERM_SIZE);
    this.permGradY = new Float64Array(PERM_SIZE);

    const source = new Int16Array(PERM_SIZE);
    for (let i = 0; i < PERM_SIZE; i++) source[i] = i;

    let s = typeof seed === 'bigint' ? seed : BigInt(seed);
    for (let i = PERM_SIZE - 1; i >= 0; i--) {
      const next = (s * LCG_MULT + LCG_INC) & MASK_64;
      s = toSignedI64(next);
      const dividend = s + 31n;
      const div = BigInt(i + 1);
      let r = Number(dividend % div);
      if (r < 0) r += i + 1;
      this.perm[i] = source[r];
      const grad = GRADIENTS_2D[this.perm[i] % GRADIENTS_BASE.length];
      this.permGradX[i] = grad.dx;
      this.permGradY[i] = grad.dy;
      source[r] = source[i];
    }
  }

  /** 2D noise in [0, 1] range. */
  sample2d(x: number, y: number): number {
    return (this.noise2(x, y) + 1.0) / 2.0;
  }

  /** Raw 2D OpenSimplex2 noise in approximately [-1, 1]. */
  private noise2(x: number, y: number): number {
    const s = SKEW_2D * (x + y);
    const xs = x + s;
    const ys = y + s;

    const xsb = fastFloor(xs);
    const ysb = fastFloor(ys);
    const xi = xs - xsb;
    const yi = ys - ysb;

    const t = (xi + yi) * UNSKEW_2D;
    const dx0 = xi - t;
    const dy0 = yi - t;

    let value = 0;

    const a0 = 2.0 / 3.0 - dx0 * dx0 - dy0 * dy0;
    if (a0 > 0) {
      const a02 = a0 * a0;
      value = a02 * a02 * this.grad(xsb, ysb, dx0, dy0);
    }

    const a1 =
      2.0 * (1.0 - 2.0 * UNSKEW_2D) * (1.0 / UNSKEW_2D + 2.0) * t +
      (-2.0 * (1.0 - 2.0 * UNSKEW_2D) * (1.0 - 2.0 * UNSKEW_2D) + a0);
    if (a1 > 0) {
      const dx1 = dx0 - (1.0 - 2.0 * UNSKEW_2D);
      const dy1 = dy0 - (1.0 - 2.0 * UNSKEW_2D);
      const a12 = a1 * a1;
      value += a12 * a12 * this.grad(xsb + 1, ysb + 1, dx1, dy1);
    }

    if (dy0 > dx0) {
      const dx2 = dx0 + UNSKEW_2D;
      const dy2 = dy0 + (UNSKEW_2D - 1.0);
      const a2 = 2.0 / 3.0 - dx2 * dx2 - dy2 * dy2;
      if (a2 > 0) {
        const a22 = a2 * a2;
        value += a22 * a22 * this.grad(xsb, ysb + 1, dx2, dy2);
      }
    } else {
      const dx2 = dx0 + (UNSKEW_2D - 1.0);
      const dy2 = dy0 + UNSKEW_2D;
      const a2 = 2.0 / 3.0 - dx2 * dx2 - dy2 * dy2;
      if (a2 > 0) {
        const a22 = a2 * a2;
        value += a22 * a22 * this.grad(xsb + 1, ysb, dx2, dy2);
      }
    }

    return value * NORM_2D;
  }

  private grad(xsv: number, ysv: number, dx: number, dy: number): number {
    const x0 = xsv & PERM_MASK;
    const y0 = ysv & PERM_MASK;
    const index = ((this.perm[x0] & PERM_MASK) ^ y0) & PERM_MASK;
    const idx = this.perm[index] & PERM_MASK;
    return this.permGradX[idx] * dx + this.permGradY[idx] * dy;
  }
}
